import _ from 'lodash';
import SimpleReactValidator from 'pu-utils/simple-react-validator';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { ERROR, PRICE_SELECTED, QUOTED } from 'pu-data/QuoteSteps';
import { parameterForceRequote } from 'pu-data/Utils';
import { getQuote, emailQuote } from 'api/apiQuote';
import { selectAssemblyDataForQuote, selectAssemblyDataForValidate } from 'us_pcb_assembly/store/selector';
import initialState from 'us_pcb_assembly/data/initialState';

export const doQuote = createAsyncThunk(
  'assembly/doQuote',
  async (args, thunkAPI) => {
    const { variant, parameters, suppliers } = selectAssemblyDataForQuote(thunkAPI.getState());
    const token = document.getElementsByName('authenticity_token')[0].value;
    const data = {};
    suppliers.forEach((s) => {
      data[s] = parameters;
    });

    const promise = getQuote({
      data, token, variant, quoteNumber: parameters.product_quote_number,
    });

    return promise
      .then((res) => res.data)
      .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
  },
);

export const doEmailQuote = createAsyncThunk('assembly/doEmailQuote', async (args, thunkAPI) => {
  const { data, token } = args;
  const promise = emailQuote({ data, token });
  return promise
    .then((res) => res.data)
    .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
});

export const validator = new SimpleReactValidator({});

const usAssemblySlice = createSlice({
  name: 'usAssemblySlice',
  initialState: {
    ...initialState,
  },
  reducers: {
    changeParameter(state, action) {
      const { name, value } = action.payload
      switch (name) {
        case 'qty1':
          state.parameters.quantities[0] = value
          break;
        case 'qty2':
          state.parameters.quantities[1] = value
          break;
        case 'qty3':
          state.parameters.quantities[2] = value
          break;
        case 'qty4':
          state.parameters.quantities[3] = value
          break;
        default:
          state.parameters[name] = value
      }

      state.parametersChanged = parameterForceRequote(name);
    },
    setPriceSelected(state, action) {
      const { row_selected, column_selected, supplier, currentVariant } = action.payload
      state.parameters.row_selected = row_selected
      state.parameters.column_selected = column_selected
      state.parameters.supplier_selected = supplier
      state.currentVariant = currentVariant

      state.quoteStep = PRICE_SELECTED
    },
    deleteAssembly(state) {
      const { part_number, revision, filename } = state.parameters;
      validator.hideMessages();
      const newState = _.cloneDeep({ ...defaultState, validatorFlag: false });
      newState.parameters.part_number = part_number;
      newState.parameters.revision = revision;
      newState.parameters.filename = filename;
      newState.lineItemId = state.lineItemId;
      newState.destroy = true;
      newState.zip_file_input_id = state.zip_file_input_id;
      return newState;
    },
    updateValidatorFlag(state) {
      state.validatorFlag = !state.validatorFlag;
    },
    // Accepts an array of field names to show error messages for.
    // If no fields are passed, shows all error messages
    // This updates the state to show the error messages and rerenders the component
    showValidatorErrorMessages(state, action) {
      const validatedFields = action.payload;

      if (validatedFields && validatedFields.length > 0) {
        validatedFields.forEach((name) => {
          validator.showMessageFor(name);
        });
      } else {
        validator.showMessages();
      }

      state.validatorFlag = true;
    },
    hideValidatorErrorMessages(state) {
      state.validatorFlag = false;
    },
    addErrors(state, action) {
      if (typeof action.payload === 'string') {
        state.errors = [action.payload];
      } else if (Array.isArray(action.payload)) {
        state.errors = action.payload;
      } else if (action.payload.errors) {
        state.errors = action.payload.errors;
      }
    },
    dismissErrors(state) {
      state.errors = []
    }
  },
  extraReducers(builder) {
    builder
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.loading = false;
        state.parametersChanged = false;

        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.supplier_selected = '';

        if (!quote || !quote.line_items) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        state.quote = quote;
      })
      .addCase(doQuote.rejected, (state) => {
        state.loading = false;
        state.quote = {};
        state.quoteStep = ERROR;
      })
      .addCase(doEmailQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doEmailQuote.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(doEmailQuote.rejected, (state, action) => {
        state.loading = false;
      })
  },
})

export const {
  changeParameter,
  setPriceSelected,
  updateValidatorFlag,
  showValidatorErrorMessages,
  hideValidatorErrorMessages,
  addErrors,
  dismissErrors,
} = usAssemblySlice.actions;

export default usAssemblySlice.reducer;

export const validate = () => (dispatch, getState) => {
  const { quoteStep, parametersChanged } = selectAssemblyDataForValidate(getState())

  let validateResult = true;

  if (quoteStep === PRICE_SELECTED) {
    if (!validator.allValid()) {
      validator.showMessages();
      dispatch(updateValidatorFlag());
      dispatch(changeTab(TAB_ASSEMBLY));
      dispatch(showErrors('Please fix the errors in Assembly form'));
      validateResult = false;
      setTimeout(() => {
        document.querySelector('.pcb-assembly .srv-validation-message').scrollIntoView(false);
      }, 500);
    } else {
      if (parametersChanged) {
        dispatch(showErrors('One or more parameters have changed. Please get a new Assembly quote'));
        dispatch(changeTab(TAB_ASSEMBLY));
        validateResult = false;
      }
    }
  }

  return validateResult;
}